import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { OTHER_GENDER } from 'constants/user'
import { strings } from 'helpers'
import { trim } from 'helpers/strings'
import { useBreakpoints } from 'hooks'

import CloseIcon from 'components/CloseIcon'
import Select from './Select'

const commonGenders = ['M', 'F', 'non_binary', 'everyone']

const GenderSelect = ({
  name,
  defaultLabel,
  fullOptionsTitle,
  options,
  value,
  multiSelect,
  onChange,
  moreLabel = 'More',
}) => {
  const { md } = useBreakpoints()
  const [showAllOptions, setShowAllOptions] = useState(false)
  const [baseSelection, setBaseSelection] = useState(multiSelect ? value?.[0] : value)

  const { commonOptions } = useMemo(() => {
    const commonOptions = [
      ...options.filter(({ value }) => commonGenders.includes(value)),
      {
        label: moreLabel,
        value: OTHER_GENDER,
      },
    ]

    return { commonOptions }
  }, [options, moreLabel])

  const { selectedLabel, allOptions } = useMemo(() => {
    const valueList = multiSelect ? value : [value]
    const allOptions = options.map(option => ({
      ...option,
      selected: valueList.includes(option.value),
    }))

    const selectedLabel = trim(
      allOptions
        .filter(({ selected }) => selected)
        .map(({ label }) => label)
        .join(', '),
      md ? 65 : 40
    )

    return {
      selectedLabel,
      allOptions,
    }
  }, [value, multiSelect, options, md])

  const handleBaseChange = useCallback(
    e => {
      const value = e.target.value
      setBaseSelection(value)

      if (value === OTHER_GENDER) {
        return
      }

      if (!multiSelect) {
        onChange(e)
      } else {
        onChange({
          target: {
            name,
            value: [value],
          },
        })
      }
    },
    [name, multiSelect, onChange]
  )

  const handleChange = useCallback(
    selectedValue => {
      if (!multiSelect) {
        onChange({
          target: {
            name,
            value: selectedValue,
          },
        })
        setShowAllOptions(false)
        return
      }

      const updatedValues = value.includes(selectedValue)
        ? value.filter(v => v !== selectedValue)
        : [...value, selectedValue]
      onChange({
        target: {
          name,
          value: updatedValues,
        },
      })
    },
    [name, value, multiSelect, onChange]
  )

  useEffect(() => {
    if (baseSelection === OTHER_GENDER) {
      setShowAllOptions(true)
      setBaseSelection('')
    }
  }, [baseSelection])

  return (
    <div>
      <Select
        name={name}
        defaultLabel={defaultLabel}
        selectedLabel={selectedLabel}
        options={commonOptions}
        onChange={handleBaseChange}
        value={strings.valueOrEmptyString(baseSelection)}
      />
      {showAllOptions && (
        <div className="absolute inset-0 z-20 bg-black w-full h-full flex flex-col text-white uppercase">
          <div className="px-4 mb-4">
            <div className="flex w-full justify-end mb-3">
              <button
                className="bg-white bg-opacity-20 w-7 h-7 rounded-full flex items-center justify-center hover:opacity-70"
                onClick={() => setShowAllOptions(false)}
              >
                <CloseIcon className="w-3 h-3" />
              </button>
            </div>
            <div className="text-center text-xl font-semibold uppercase font-ownersWide">
              {fullOptionsTitle}
            </div>
          </div>
          <ul className="grow-1 overflow-y-scroll">
            {allOptions.map(option => (
              <li
                key={option.value}
                className="px-5 font-medium cursor-pointer hover:bg-gray-500"
                onClick={() => handleChange(option.value)}
              >
                <div className="flex justify-between border-0 border-solid border-b border-white border-opacity-5 py-3.5">
                  <div className="opacity-50">{option.label}</div>
                  {option.selected && <div className="text-sm font-bold">✓</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default GenderSelect
